import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  Theme,
  useTheme,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { User } from '@domain/account/models/User';
import { container } from '@infrastructure/container';
import { useAuth } from '@shared/hooks/useAuth';
import { useImpersonation } from '@shared/hooks/useImpersonation';
import { useToast } from '@shared/hooks/useToast';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useQueryClient } from '@tanstack/react-query';
import DialogHeader from '@shared/ui/dialog/DialogHeader';

const SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due', 'unpaid', 'canceled', 'incomplete', 'paused'];

const formatSubscriptionStatus = (status: string) => status.replace(/_/g, ' ');

const subscriptionStatusColor = (theme: Theme, status: string): string => {
  if (status === 'active') return theme.palette.success.main;
  if (status === 'trialing') return theme.palette.info.light;
  if (status === 'past_due') return theme.palette.warning.main;
  if (status === 'unpaid') return theme.palette.warning.main;
  if (status === 'canceled') return theme.palette.error.main;
  if (status === 'incomplete') return theme.palette.info.main;
  if (status === 'paused') return theme.palette.info.light;
  return theme.palette.info.light;
};

const ImpersonationPanel: React.FC = () => {

  const theme = useTheme();

  const { user } = useAuth();
  const { canImpersonate, isImpersonating, startImpersonation, detectImpersonatorCapability } = useImpersonation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 300);
  const searchQuery = debouncedSearch.trim();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const shouldLoadUsers = Boolean(searchQuery) || showAll || selectedStatuses.length > 0;

  useEffect(() => {
    if (canImpersonate) {
      return;
    }

    detectImpersonatorCapability();
  }, [canImpersonate, detectImpersonatorCapability]);

  useEffect(() => {
    if (!isOpen || !canImpersonate) {
      return;
    }

    container.authRepository.prefetchUsers();
  }, [canImpersonate, isOpen]);

  useEffect(() => {
    if (!isOpen || !canImpersonate || !shouldLoadUsers) {
      setUsers([]);
      setIsLoadingUsers(false);
      setLoadError(null);
      return;
    }

    let cancelled = false;

    const loadUsers = async () => {
      setIsLoadingUsers(true);
      setLoadError(null);
      try {
        const result = await container.authRepository.listUsers(
          searchQuery || undefined,
          selectedStatuses.length > 0 ? selectedStatuses : undefined,
        );
        if (cancelled) return;

        if (result.status === 'error' || !result.data) {
          setLoadError(result.status === 'error' ? (result.error.message || 'Failed to list users') : 'Failed to list users');
          setUsers([]);
          return;
        }

        setUsers(result.data.filter((candidate) => candidate.id !== user?.id));
      } catch {
        if (!cancelled) {
          setLoadError('Failed to list users');
          setUsers([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingUsers(false);
        }
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [canImpersonate, isOpen, searchQuery, selectedStatuses, shouldLoadUsers, user?.id]);

  const handleClose = () => {
    setIsOpen(false);
    setSearchInput('');
    setSelectedStatuses([]);
    setShowAll(false);
    setUsers([]);
    setLoadError(null);
  };

  const handleImpersonate = async (target: User) => {
    setIsSwitching(true);
    try {
      await startImpersonation(target.id);
      await queryClient.invalidateQueries();
      toast.success(`Now viewing as ${target.name || target.email}`);
      handleClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start impersonation');
    } finally {
      setIsSwitching(false);
    }
  };

  if (!canImpersonate || isImpersonating) {
    return null;
  }

  const showAllUsers = () => {
    setSearchInput('');
    setShowAll(true);
    setIsLoadingUsers(true);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setIsOpen(true)}
      >
        Impersonate
      </Button>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader>
          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder="Search by name, email, or company…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton onClick={handleClose} aria-label="Close">
                <Close />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              {SUBSCRIPTION_STATUSES.map((status) => (
                <Chip
                  key={status}
                  label={formatSubscriptionStatus(status)}
                  size="small"
                  clickable
                  color={selectedStatuses.includes(status) ? 'primary' : 'default'}
                  onClick={() => {
                    if (selectedStatuses.includes(status)) {
                      setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
                    } else {
                      setSelectedStatuses([...selectedStatuses, status]);
                    }
                  }}
                />
              ))}
            </Stack>

          </Stack>
        </DialogHeader>
        <DialogContent sx={{ p: 0 }}>

          {loadError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {loadError}
            </Typography>
          )}

          {!shouldLoadUsers ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Button variant="text" size="small" onClick={showAllUsers}>
                Show all users
              </Button>
            </Box>
          ) : (
            <List disablePadding>
              {!isLoadingUsers && users.length === 0 && !loadError && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No users found
                  </Typography>
                </Box>
              )}

              {isLoadingUsers ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <CircularProgress size={16} />
                </Box>
              ) : (users.map((candidate) => (
                <ListItem
                  key={candidate.id}
                  divider
                  alignItems="flex-start"
                  secondaryAction={
                    <Button
                      variant="text"
                      size="small"
                      disabled={isSwitching}
                      onClick={() => handleImpersonate(candidate)}
                    >
                      Impersonate
                    </Button>
                  }
                  sx={{ px: 4, py: 2 }}
                >
                  <ListItemText
                    primary={
                      <Stack gap={1} direction="row">
                        <Typography variant="body2" color="text.primary" component="span">
                          {candidate.name}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" component="span">
                          ({candidate.email})
                        </Typography>

                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mt={1}>
                        {candidate.companies?.map((company) => (
                          <Stack
                            direction="row"
                            gap={0}
                            alignItems="center"
                            sx={{
                              backgroundColor: theme.palette.background.default,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: '8px',
                              p: 0.5,
                            }}
                          >

                            <Typography variant="body2" color="text.secondary" component="span">
                              {company.name}
                            </Typography>

                            <Box sx={{
                              margin: 0.5,
                              width: 10,
                              height: 10,
                              backgroundColor: subscriptionStatusColor(theme, company.subscriptionStatus || ''),
                              borderRadius: '50%'
                            }} >

                            </Box>

                          </Stack>
                        ))}
                      </Stack>
                    }
                  />
                </ListItem>
              )))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImpersonationPanel;
